const CartElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
const CART_FEEDBACK_KEY = 'totf-cart-feedback';
const CHECKOUT_RECOVERY_KEY = 'totf-selected-checkout-recovery-v1';
const CHECKOUT_RECOVERY_TTL = 24 * 60 * 60 * 1000;
export const CART_SELECTION_KEY = 'totf-cart-selection-v1';

function getRecoveryStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function removeRecoveryRecord(storage) {
  try {
    storage?.removeItem(CHECKOUT_RECOVERY_KEY);
  } catch {
    return false;
  }
  return true;
}

function writeRecoveryRecord(storage, record) {
  try {
    storage?.setItem(CHECKOUT_RECOVERY_KEY, JSON.stringify(record));
  } catch {
    return false;
  }
  return true;
}

function isPlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasStringProperties(properties) {
  if (!isPlainObject(properties)) return false;
  try {
    return Object.entries(properties).every(([, value]) => typeof value === 'string');
  } catch {
    return false;
  }
}

export function isValidRecoveryCheckoutUrl(checkoutUrl, { hostname, shopDomain } = {}) {
  if (typeof checkoutUrl !== 'string') return false;
  const allowedHosts = [hostname, shopDomain]
    .filter((host) => typeof host === 'string' && host.trim())
    .map((host) => host.trim().toLowerCase());
  try {
    const url = new URL(checkoutUrl);
    return url.protocol === 'https:'
      && allowedHosts.includes(url.host.toLowerCase())
      && (url.pathname === '/checkouts' || url.pathname.startsWith('/checkouts/'));
  } catch {
    return false;
  }
}

function isValidRecoveryRecord(record, now, allowedHosts) {
  if (!isPlainObject(record)
    || record.version !== 1
    || !['prepared', 'moved'].includes(record.state)
    || !Number.isFinite(record.createdAt)
    || !Number.isFinite(now)) return false;
  const age = now - record.createdAt;
  if (age < 0 || age > CHECKOUT_RECOVERY_TTL
    || !Array.isArray(record.lines)
    || record.lines.length === 0
    || !isValidRecoveryCheckoutUrl(record.checkoutUrl, allowedHosts)) return false;
  return record.lines.every((line) => isPlainObject(line)
    && typeof line.variantId === 'string'
    && /^\d+$/.test(line.variantId)
    && Number.isSafeInteger(line.quantity)
    && line.quantity > 0
    && hasStringProperties(line.properties));
}

export function readRecoveryRecord(storage, now = Date.now(), allowedHosts = {}) {
  let record;
  try {
    const raw = storage?.getItem(CHECKOUT_RECOVERY_KEY);
    if (!raw) return null;
    record = JSON.parse(raw);
  } catch {
    removeRecoveryRecord(storage);
    return null;
  }
  if (!isValidRecoveryRecord(record, now, allowedHosts)) {
    removeRecoveryRecord(storage);
    return null;
  }
  return record;
}

const SELECTED_CART_MUTATION = `
  mutation SelectedCheckout($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 250) {
          nodes {
            quantity
            merchandise { ... on ProductVariant { id } }
          }
        }
        discountCodes { code applicable }
        cost {
          subtotalAmount { amount currencyCode }
          totalAmount { amount currencyCode }
        }
      }
      userErrors { field message code }
      warnings { code target message }
    }
  }
`;

export function buildSelectedCartInput(lines, { note = '', discountCode = '', countryCode = '' } = {}) {
  const input = {
    lines: lines.map((line) => ({
      merchandiseId: `gid://shopify/ProductVariant/${line.variantId}`,
      quantity: line.quantity,
      attributes: Object.entries(line.properties)
        .filter(([, value]) => value !== '')
        .map(([key, value]) => ({ key, value: String(value) }))
    }))
  };
  if (note.trim()) input.note = note.trim();
  if (discountCode.trim()) input.discountCodes = [discountCode.trim()];
  if (countryCode) input.buyerIdentity = { countryCode };
  return input;
}

export async function createStorefrontCart({
  endpoint,
  token,
  input,
  fetchImpl = fetch,
  allowDiscountWarnings = false
}) {
  const response = await fetchImpl(endpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token
    },
    body: JSON.stringify({ query: SELECTED_CART_MUTATION, variables: { input } })
  });
  const payload = await response.json();
  const result = payload?.data?.cartCreate;
  const errors = [...(payload.errors ?? []), ...(result?.userErrors ?? [])];
  if (!response.ok || errors.length || !result?.cart?.checkoutUrl) {
    throw new Error(errors[0]?.message || 'Shopify could not prepare the selected checkout.');
  }
  const blockingWarning = result.warnings?.find((warning) => (
    !allowDiscountWarnings || !String(warning?.code ?? '').startsWith('DISCOUNT_')
  ));
  if (blockingWarning) {
    throw new Error(blockingWarning.message || 'Shopify changed the selected items. Review your cart and try again.');
  }
  const expectedLines = input.lines
    .map(({ merchandiseId, quantity }) => `${merchandiseId}:${quantity}`)
    .sort();
  const returnedLines = (result.cart.lines?.nodes ?? [])
    .map(({ merchandise, quantity }) => `${merchandise?.id}:${quantity}`)
    .sort();
  if (expectedLines.length !== returnedLines.length || expectedLines.some((line, index) => line !== returnedLines[index])) {
    throw new Error('Shopify changed the selected items. Review your cart and try again.');
  }
  return result.cart;
}

export function readLineSnapshot(row) {
  const propertyNodes = row.querySelector('[data-cart-line-properties]')
    ?.content.querySelectorAll('[data-property-key]') ?? [];
  return {
    key: row.dataset.cartKey,
    variantId: row.dataset.cartVariantId,
    quantity: Number(row.dataset.cartQuantity),
    finalLinePrice: Number(row.dataset.cartLinePrice),
    properties: Object.fromEntries([...propertyNodes].map((node) => [
      node.dataset.propertyKey,
      node.dataset.propertyValue
    ]))
  };
}

export function selectionSummary(lines) {
  return lines.reduce((summary, line) => ({
    productCount: summary.productCount + 1,
    itemCount: summary.itemCount + line.quantity,
    subtotal: summary.subtotal + line.finalLinePrice
  }), { productCount: 0, itemCount: 0, subtotal: 0 });
}

export class ReadingCart extends CartElement {
  connectedCallback() {
    this.note = this.querySelector('[data-cart-note]');
    this.noteSave = this.querySelector('[data-cart-note-save]');
    this.discount = this.querySelector('[data-discount-code]');
    this.discountApply = this.querySelector('[data-discount-apply]');
    this.discountFeedback = this.querySelector('[data-discount-feedback]');
    this.feedback = this.querySelector('[data-cart-feedback]');
    this.continueSelectedCheckout = this.querySelector('[data-continue-selected-checkout]');
    this.recovery = this.querySelector('[data-checkout-recovery]');
    this.continueCheckout = this.querySelector('[data-continue-checkout]');
    this.restoreCheckoutItemsButton = this.querySelector('[data-restore-checkout-items]');
    this.dismissRecoveryButton = this.querySelector('[data-dismiss-checkout-recovery]');
    this.selectAll = this.querySelector('[data-cart-select-all]');
    this.lineSelections = [...this.querySelectorAll('[data-cart-select]')];
    this.selectionCount = this.querySelector('[data-selection-count]');
    this.selectedSubtotal = this.querySelector('[data-cart-selected-subtotal]');
    this.selectedCheckout = this.querySelector('[data-selected-checkout]');
    this.currencyCode = this.dataset.currencyCode || 'USD';
    this.moneyLocale = this.dataset.moneyLocale || 'en-US';
    this.deleteSelected = this.querySelector('[data-delete-selected]');
    this.deleteConfirmation = this.querySelector('[data-delete-confirmation]');
    this.deleteConfirmationCopy = this.querySelector('[data-delete-confirmation-copy]');
    this.deleteConfirm = this.querySelector('[data-delete-confirm]');
    this.deleteCancel = this.querySelector('[data-delete-cancel]');
    this.quantityDecrease = [...this.querySelectorAll('[data-quantity-decrease]')];
    this.quantityIncrease = [...this.querySelectorAll('[data-quantity-increase]')];
    this.requestPending = false;
    this.pendingRemovalKeys = [];
    this.storefrontPreview = null;

    this.feedback?.setAttribute('aria-live', 'polite');
    this.feedback?.setAttribute('aria-atomic', 'true');
    this.restoreFeedback();
    this.restoreCheckoutRecovery();
    this.restoreSelection();
    this.syncSelection();

    this.noteSave?.addEventListener('click', () => this.saveNote());
    this.discountApply?.addEventListener('click', () => this.previewDiscount());
    this.discount?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.previewDiscount();
      }
    });
    this.note?.addEventListener('input', () => this.syncSelection());
    this.discount?.addEventListener('input', () => this.syncSelection());
    this.lineSelections.forEach((input) => input.addEventListener('change', () => this.syncSelection()));
    this.selectAll?.addEventListener('change', () => {
      this.lineSelections.forEach((input) => { input.checked = this.selectAll.checked; });
      this.syncSelection();
    });
    this.deleteSelected?.addEventListener('click', () => this.openDeleteConfirmation(this.selectedLines));
    this.deleteCancel?.addEventListener('click', () => this.closeDeleteConfirmation());
    this.deleteConfirm?.addEventListener('click', () => this.removePendingRemoval());
    this.quantityDecrease.forEach((button) => button.addEventListener('click', () => this.changeQuantity(button, -1)));
    this.quantityIncrease.forEach((button) => button.addEventListener('click', () => this.changeQuantity(button, 1)));
    this.selectedCheckout?.addEventListener('click', () => this.prepareSelectedCheckout());
    this.restoreCheckoutItemsButton?.addEventListener('click', () => this.restoreCheckoutItems());
    this.dismissRecoveryButton?.addEventListener('click', () => this.dismissRecovery());
  }

  get root() {
    return window.Shopify?.routes?.root ?? '/';
  }

  get selectedLines() {
    return this.lineSelections.filter((input) => input.checked);
  }

  get selectedLineSnapshots() {
    return this.selectedLines
      .map((input) => input.closest('[data-cart-line]'))
      .filter(Boolean)
      .map((row) => readLineSnapshot(row));
  }

  get storefrontEndpoint() {
    const domain = this.dataset.storeDomain;
    const version = this.dataset.storefrontApiVersion;
    return domain && version ? `https://${domain}/api/${version}/graphql.json` : '';
  }

  setFeedback(message, state = '') {
    if (!this.feedback) return;
    this.feedback.textContent = message;
    this.feedback.dataset.state = state;
  }

  setDiscountFeedback(message, state = '') {
    this.setFeedback(message, state);
    if (!this.discountFeedback) return;
    this.discountFeedback.textContent = message;
    this.discountFeedback.dataset.state = state;
  }

  syncSelection({ invalidatePreview = true, updateSubtotal = true } = {}) {
    const selected = this.selectedLines.length;
    const total = this.lineSelections.length;
    const summary = selectionSummary(this.selectedLineSnapshots);
    const unavailable = selected === 0 || this.requestPending;
    if (this.selectAll) {
      this.selectAll.checked = total > 0 && selected === total;
      this.selectAll.indeterminate = selected > 0 && selected < total;
    }
    if (this.selectionCount) this.selectionCount.textContent = `${summary.productCount} of ${total} products selected`;
    if (this.selectedSubtotal && updateSubtotal) {
      this.selectedSubtotal.dataset.cents = String(summary.subtotal);
      this.selectedSubtotal.textContent = this.formatMoney(summary.subtotal);
    }
    if (this.selectedCheckout) {
      this.selectedCheckout.textContent = `Checkout selected · ${summary.itemCount} ${summary.itemCount === 1 ? 'item' : 'items'}`;
      this.selectedCheckout.disabled = unavailable;
    }
    if (this.deleteSelected) this.deleteSelected.disabled = unavailable;
    if (this.discountApply) this.discountApply.disabled = unavailable;
    this.persistSelection();
    if (invalidatePreview) this.invalidateStorefrontPreview();
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(this.moneyLocale, {
      style: 'currency',
      currency: this.currencyCode
    }).format(cents / 100);
  }

  restoreSelection() {
    const currentKeys = new Set(this.lineSelections.map((input) => input.dataset.cartKey));
    try {
      const raw = window.sessionStorage?.getItem(CART_SELECTION_KEY);
      if (!raw) throw new Error('No saved selection');
      const savedKeys = JSON.parse(raw);
      if (!Array.isArray(savedKeys)) throw new Error('Invalid saved selection');
      const selectedKeys = new Set(savedKeys.filter((key) => currentKeys.has(key)));
      this.lineSelections.forEach((input) => { input.checked = selectedKeys.has(input.dataset.cartKey); });
    } catch {
      this.lineSelections.forEach((input) => { input.checked = true; });
    }
  }

  persistSelection() {
    try {
      window.sessionStorage?.setItem(CART_SELECTION_KEY, JSON.stringify(this.selectedLines.map((input) => input.dataset.cartKey)));
    } catch {
      return;
    }
  }

  invalidateStorefrontPreview() {
    this.storefrontPreview = null;
  }

  selectedCartSignature(lines = this.selectedLineSnapshots) {
    return JSON.stringify({
      lines: lines.map(({ key, quantity }) => [key, quantity]),
      note: this.note?.value.trim() ?? '',
      discountCode: this.discount?.value.trim() ?? ''
    });
  }

  selectedCartInput(lines = this.selectedLineSnapshots) {
    return buildSelectedCartInput(lines, {
      note: this.note?.value ?? '',
      discountCode: this.discount?.value ?? '',
      countryCode: this.dataset.countryCode ?? ''
    });
  }

  openDeleteConfirmation(inputs) {
    const pendingRemovalKeys = inputs
      .map((input) => input?.dataset.cartKey)
      .filter(Boolean);
    if (!pendingRemovalKeys.length || !this.deleteConfirmation) return;
    this.pendingRemovalKeys = pendingRemovalKeys;
    if (this.deleteConfirmationCopy) {
      this.deleteConfirmationCopy.textContent = pendingRemovalKeys.length === 1
        ? 'This item will be removed. Any question and card spread attached to it will be lost.'
        : `${pendingRemovalKeys.length} items will be removed. Any questions and card spreads attached to them will be lost.`;
    }
    this.deleteConfirmation.hidden = false;
    this.deleteConfirmation.focus();
  }

  closeDeleteConfirmation() {
    if (this.deleteConfirmation) this.deleteConfirmation.hidden = true;
    this.pendingRemovalKeys = [];
    this.deleteSelected?.focus();
  }

  setRequestBusy(busy) {
    this.requestPending = busy;
    if (this.deleteConfirm) this.deleteConfirm.disabled = busy;
    if (this.deleteCancel) this.deleteCancel.disabled = busy;
    if (this.noteSave) this.noteSave.disabled = busy;
    if (this.selectAll) this.selectAll.disabled = busy;
    if (this.note) this.note.disabled = busy;
    if (this.discount) this.discount.disabled = busy;
    if (this.restoreCheckoutItemsButton) this.restoreCheckoutItemsButton.disabled = busy;
    if (this.dismissRecoveryButton) this.dismissRecoveryButton.disabled = busy;
    this.lineSelections.forEach((input) => { input.disabled = busy; });
    [...this.quantityDecrease, ...this.quantityIncrease].forEach((button) => { button.disabled = busy; });
    this.syncSelection({ invalidatePreview: false, updateSubtotal: !this.storefrontPreview });
  }

  async parseRequiredCartResponse(response) {
    let payload;
    try {
      payload = await response.json();
    } catch {
      throw new Error('Shopify returned an unreadable cart response, so we could not confirm the update. Please try again.');
    }
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      throw new Error('Shopify returned an invalid cart response, so we could not confirm the update. Please try again.');
    }
    return payload;
  }

  restoreCheckoutRecovery() {
    const record = readRecoveryRecord(getRecoveryStorage(), Date.now(), {
      hostname: window.location?.host,
      shopDomain: this.dataset.storeDomain
    });
    const movedRecord = record?.state === 'moved' ? record : null;
    if (this.recovery) this.recovery.hidden = !movedRecord;
    if (movedRecord && this.continueCheckout) this.continueCheckout.href = movedRecord.checkoutUrl;
    return movedRecord;
  }

  async restoreCheckoutItems() {
    if (this.requestPending) return false;
    const record = this.restoreCheckoutRecovery();
    if (!record) return false;

    this.setRequestBusy(true);
    this.setFeedback('Restoring your selected checkout items…');
    try {
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: record.lines.map(({ variantId, quantity, properties }) => ({
            id: variantId,
            quantity,
            properties
          }))
        })
      });
      const payload = await this.parseRequiredCartResponse(response);
      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Shopify could not restore the selected checkout items. Please try again.');
      }
      removeRecoveryRecord(getRecoveryStorage());
      if (this.recovery) this.recovery.hidden = true;
      this.persistFeedback('Selected checkout items restored to your cart.', 'success');
      this.setFeedback('Selected checkout items restored to your cart.', 'success');
      this.refreshCart();
      return true;
    } catch (error) {
      this.setFeedback(error?.message || 'Shopify could not restore the selected checkout items. Please try again.', 'error');
      return false;
    } finally {
      this.setRequestBusy(false);
    }
  }

  dismissRecovery() {
    if (this.requestPending) return false;
    removeRecoveryRecord(getRecoveryStorage());
    if (this.recovery) this.recovery.hidden = true;
    return true;
  }

  async updateCart(updates, successMessage) {
    if (this.requestPending) return false;
    this.setRequestBusy(true);
    this.setFeedback('Updating your cart…');
    try {
      const response = await fetch(`${this.root}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      const payload = await this.parseRequiredCartResponse(response);
      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Shopify could not update the cart. Please try again.');
      }
      this.persistFeedback(successMessage, 'success');
      this.setFeedback(successMessage, 'success');
      this.refreshCart();
      return true;
    } catch (error) {
      this.setFeedback(error?.message || 'We could not connect to Shopify. Your cart is unchanged; please try again.', 'error');
      return false;
    } finally {
      this.setRequestBusy(false);
    }
  }

  async removeSelected() {
    return this.removePendingRemoval();
  }

  async removePendingRemoval() {
    const updates = Object.fromEntries(this.pendingRemovalKeys.map((key) => [key, 0]));
    if (!Object.keys(updates).length) return false;
    const removed = await this.updateCart(updates, 'Selected items removed. Cart totals are updated.');
    if (removed) this.pendingRemovalKeys = [];
    return removed;
  }

  async changeQuantity(button, delta) {
    const row = button?.closest('[data-cart-line]');
    const key = row?.dataset.cartKey;
    const quantity = Number(row?.dataset.cartQuantity);
    if (!key || !Number.isInteger(quantity) || !Number.isInteger(delta)) return false;
    this.invalidateStorefrontPreview();
    const nextQuantity = Math.max(0, quantity + delta);
    if (nextQuantity === 0) {
      const selection = row.querySelector('[data-cart-select]');
      this.openDeleteConfirmation(selection ? [selection] : []);
      return false;
    }
    return this.updateCart({ [key]: nextQuantity }, 'Quantity updated. Cart totals are updated.');
  }

  restoreFeedback() {
    try {
      const raw = window.sessionStorage?.getItem(CART_FEEDBACK_KEY);
      if (!raw) return;
      window.sessionStorage.removeItem(CART_FEEDBACK_KEY);
      const saved = JSON.parse(raw);
      if (typeof saved?.message === 'string') this.setFeedback(saved.message, saved.state ?? '');
    } catch {
      // Feedback persistence is optional; the cart remains usable without storage.
    }
  }

  persistFeedback(message, state) {
    try {
      window.sessionStorage?.setItem(CART_FEEDBACK_KEY, JSON.stringify({ message, state }));
    } catch {
      // The live message remains available if session storage is unavailable.
    }
  }

  refreshCart() {
    window.location.reload();
  }

  async saveNote() {
    if (!this.note || !this.noteSave) return;
    const original = this.noteSave.textContent;
    this.noteSave.disabled = true;
    this.noteSave.textContent = 'Saving…';
    this.setFeedback('');
    try {
      const response = await fetch(`${this.root}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note: this.note.value })
      });
      if (!response.ok) throw new Error('Your note could not be saved. Please try again.');
      this.setFeedback('Your order note is saved.', 'success');
    } catch (error) {
      this.setFeedback(error.message, 'error');
    } finally {
      this.noteSave.disabled = false;
      this.noteSave.textContent = original;
    }
  }

  async previewDiscount() {
    if (this.requestPending) return;
    if (!this.selectedLines.length) {
      this.setDiscountFeedback('Select at least one item to preview a discount.', 'error');
      return;
    }
    const code = this.discount?.value.trim();
    if (!code) {
      this.setDiscountFeedback('Enter a discount code.', 'error');
      this.discount?.focus();
      return;
    }

    if (!this.dataset.storefrontToken || !this.storefrontEndpoint) {
      this.setDiscountFeedback('Selected checkout is unavailable until the Storefront connection is configured.', 'error');
      return;
    }

    const selectedSnapshots = this.selectedLineSnapshots;
    const signature = this.selectedCartSignature(selectedSnapshots);
    const original = this.discountApply?.textContent;
    if (this.discountApply) {
      this.discountApply.textContent = 'Applying…';
    }
    this.setRequestBusy(true);
    this.setDiscountFeedback('Applying discount code…');
    try {
      const cart = await createStorefrontCart({
        endpoint: this.storefrontEndpoint,
        token: this.dataset.storefrontToken,
        input: this.selectedCartInput(selectedSnapshots),
        allowDiscountWarnings: true
      });
      if (this.selectedCartSignature() !== signature) {
        this.setDiscountFeedback('Your selection or checkout details changed. Apply the discount again.', 'error');
        return false;
      }
      this.storefrontPreview = { signature, cart };
      if (!cart.discountCodes?.[0]?.applicable) {
        this.setDiscountFeedback('That discount code is not applicable to the selected items. Check its conditions and try again.', 'error');
        return;
      }

      const total = cart.cost?.totalAmount;
      const cents = Math.round(Number(total?.amount) * 100);
      if (this.selectedSubtotal && Number.isFinite(cents)) {
        if (total.currencyCode) this.currencyCode = total.currencyCode;
        this.selectedSubtotal.dataset.cents = String(cents);
        this.selectedSubtotal.textContent = this.formatMoney(cents);
      }
      this.setDiscountFeedback('Discount code applied to the selected items.', 'success');
    } catch {
      this.storefrontPreview = null;
      this.setDiscountFeedback('We could not connect to Shopify. Your cart is unchanged; please try again.', 'error');
    } finally {
      this.setRequestBusy(false);
      if (this.discountApply) {
        this.discountApply.textContent = original;
      }
    }
  }

  async prepareSelectedCheckout() {
    if (this.requestPending) return false;
    const selectedSnapshots = this.selectedLineSnapshots;
    if (!selectedSnapshots.length) {
      this.setFeedback('Select at least one item to checkout.', 'error');
      return false;
    }
    if (!this.dataset.storefrontToken || !this.storefrontEndpoint) {
      this.setFeedback('Selected checkout is unavailable until the Storefront connection is configured.', 'error');
      return false;
    }

    const signature = this.selectedCartSignature(selectedSnapshots);
    let recoveryRecord;
    this.setRequestBusy(true);
    this.setFeedback('Preparing your selected checkout…');
    if (this.continueSelectedCheckout) this.continueSelectedCheckout.hidden = true;
    try {
      const cart = this.storefrontPreview?.signature === signature
        ? this.storefrontPreview.cart
        : await createStorefrontCart({
          endpoint: this.storefrontEndpoint,
          token: this.dataset.storefrontToken,
          input: this.selectedCartInput(selectedSnapshots)
        });
      if (this.selectedCartSignature() !== signature) {
        throw new Error('Your selection or checkout details changed. Review them and try again.');
      }
      recoveryRecord = {
        version: 1,
        state: 'prepared',
        createdAt: Date.now(),
        checkoutUrl: cart.checkoutUrl,
        lines: selectedSnapshots.map(({ variantId, quantity, properties }) => ({
          variantId,
          quantity,
          properties
        }))
      };
      writeRecoveryRecord(getRecoveryStorage(), recoveryRecord);

      const updates = Object.fromEntries(selectedSnapshots.map(({ key }) => [key, 0]));
      const response = await fetch(`${this.root}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      const payload = await this.parseRequiredCartResponse(response);
      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Shopify could not move the selected items to checkout.');
      }

      recoveryRecord.state = 'moved';
      writeRecoveryRecord(getRecoveryStorage(), recoveryRecord);
      if (this.continueSelectedCheckout) this.continueSelectedCheckout.href = cart.checkoutUrl;
      this.navigateToCheckout(cart.checkoutUrl);
      return true;
    } catch (error) {
      if (recoveryRecord?.state === 'moved' && this.continueSelectedCheckout) {
        this.continueSelectedCheckout.hidden = false;
        this.setFeedback('Your checkout is ready. Use Continue to checkout to proceed.', 'error');
      } else {
        this.setFeedback(error?.message || 'Shopify could not prepare the selected checkout. Please try again.', 'error');
      }
      return false;
    } finally {
      this.setRequestBusy(false);
    }
  }

  navigateToCheckout(url) {
    window.location.assign(url);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('reading-cart')) {
  customElements.define('reading-cart', ReadingCart);
}
